import { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import MarketDataItem from "ui/marketdata-page/rendering-parts/MarketDataItem";
import { accountThunks } from "actions-thunks/account-thunks";
import {
  getBundleDisplayLabelFromDoc,
  getBundleName,
  getMarketWithName,
  getTotalPriceHandler,
} from "ui/marketdata-page/helpers";

import "./styles.scss";
import "ui/marketdata-page/marketdata.scss";

const MarketDataSelection = ({ selectedPlatform, onMarketsChange, currentAccount }) => {
  const dispatch = useDispatch();
  const { marketdataProviders, tradingPlatforms, marketBundles } = useSelector((state) => state.marketdata);

  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [buttonItem, setButtonItem] = useState(null);

  // Fetch market data providers if not loaded (bundles come from the same endpoint)
  useEffect(() => {
    if (!marketdataProviders) {
      dispatch(accountThunks.setEntityTC("marketdataproviders"));
    }
    if (!tradingPlatforms) {
      dispatch(accountThunks.setEntityTC("tradingplatforms"));
    }
  }, [marketdataProviders, tradingPlatforms, dispatch]);

  // Find trading platform reference from selected platform
  const tradingPlatformRef = useMemo(() => {
    if (!selectedPlatform || !tradingPlatforms) return null;
    
    const platform = tradingPlatforms.find(
      (tp) =>
        tp.software === selectedPlatform.software &&
        tp.softwareType === selectedPlatform.softwareType &&
        tp.active
    );
    
    return platform?.reference || null;
  }, [selectedPlatform, tradingPlatforms]);

  // Filter market data providers for the selected platform
  const filterMarketsToTradingPlatform = useMemo(() => {
    if (!marketdataProviders || !tradingPlatformRef) return [];
    
    return marketdataProviders.filter((m) =>
      m.tradingPlatformRefs.includes(tradingPlatformRef)
    );
  }, [marketdataProviders, tradingPlatformRef]);

  // Initialize with current account's market data if available (only once when platform is selected)
  useEffect(() => {
    if (selectedPlatform && currentAccount?.extensions) {
      const marketDataExt = currentAccount.extensions.find((e) => e.code === "marketdata");
      if (marketDataExt?.data) {
        // Extract market names from the extension data
        const currentMarkets = Array.isArray(marketDataExt.data)
          ? marketDataExt.data.map((m) => m.market || m.name)
          : [];
        setSelectedMarkets(currentMarkets);
      } else {
        setSelectedMarkets([]);
      }
    } else {
      setSelectedMarkets([]);
    }
  }, [selectedPlatform, currentAccount]);

  // Notify parent when markets change
  useEffect(() => {
    if (onMarketsChange) {
      onMarketsChange(selectedMarkets);
    }
  }, [selectedMarkets, onMarketsChange]);

  const handleMarketStatusChange = (name, value, reference, depRef) => {
    let newMarkets = [...selectedMarkets];

    if (value) {
      // Handle mutually exclusive markets when checking an item
      const currentMarketItem = filterMarketsToTradingPlatform.find((m) => m.name === name);
      if (currentMarketItem?.mutuallyExclusive?.length) {
        // Remove all mutually exclusive markets from the selection
        currentMarketItem.mutuallyExclusive.forEach((exclusiveRef) => {
          const exclusiveMarket = filterMarketsToTradingPlatform.find(
            (m) => m.reference === exclusiveRef
          );
          if (exclusiveMarket) {
            newMarkets = newMarkets.filter((m) => m !== exclusiveMarket.name);
          }
        });
      }

      // Add dependencies
      if (depRef && depRef.length) {
        depRef.forEach((ref) => {
          const depMarket = filterMarketsToTradingPlatform.find((m) => m.reference === ref);
          if (depMarket && !newMarkets.includes(depMarket.name)) {
            newMarkets.push(depMarket.name);
          }
        });
      }

      // Add the selected market
      if (!newMarkets.includes(name)) {
        newMarkets.push(name);
      }
    } else {
      // Remove the market and its dependencies
      if (reference) {
        const marketsToFilter = new Set(
          filterMarketsToTradingPlatform
            .filter((el) => el.dependencyRefs.includes(reference))
            .map((el) => el.name)
        );
        newMarkets = newMarkets.filter((m) => !marketsToFilter.has(m));
      }
      newMarkets = newMarkets.filter((m) => m !== name);
    }

    setSelectedMarkets(newMarkets);
  };

  // Get unique groups from filtered markets
  const groups = useMemo(() => {
    if (!filterMarketsToTradingPlatform) return [];
    const uniqueGroups = new Set();
    filterMarketsToTradingPlatform.forEach((m) => {
      if (m.group) uniqueGroups.add(m.group);
    });
    return Array.from(uniqueGroups);
  }, [filterMarketsToTradingPlatform]);

  // Prepare bundle buttons
  const bundleButtons = useMemo(() => {
    if (!marketBundles || !selectedPlatform) return [];
    
    const unitedPlatformName = `${selectedPlatform.software} ${selectedPlatform.softwareType}`;
    marketBundles.sort((a, b) => a.order - b.order);

    // Filter bundles based on platforms.visibleFor and platforms.hiddenFor
    const filteredBundles = marketBundles.filter(bundle => {
      const { platforms } = bundle;
      if (!platforms) return true;

      const visibleFor = platforms.visibleFor || [];
      const hiddenFor = platforms.hiddenFor || [];

      if (hiddenFor.includes(unitedPlatformName)) return false;
      if (visibleFor.includes("*")) return true;
      return visibleFor.includes(unitedPlatformName);
    });

    const buttons = filteredBundles.map((m, inx) => ({
      name: m.name,
      displayLabel: getBundleDisplayLabelFromDoc(m),
      value: inx,
    }));

    buttons.push({
      name: "Custom",
      displayLabel: "Custom",
      value: buttons.length,
      disabled: false,
    });

    return buttons;
  }, [marketBundles, selectedPlatform]);

  // Update button item when markets change
  useEffect(() => {
    if (selectedMarkets.length === 0) {
      setButtonItem(null);
      return;
    }

    const bundleName = getBundleName({
      markets: selectedMarkets,
      marketdataProviders,
      currentAccount: { ...currentAccount, software: selectedPlatform.software, softwareType: selectedPlatform.softwareType },
      tradingPlatforms,
      marketBundles,
    });
    setButtonItem(bundleName);
  }, [selectedMarkets, marketdataProviders, tradingPlatforms, marketBundles, selectedPlatform, currentAccount]);

  // Handle bundle selection
  const handleRequestMarketBundleChange = ({ name }) => {
    if (name === "Custom") {
      // Custom bundle - keep current selection
      return;
    }

    const markets = getMarketWithName({ filterMarketsToTradingPlatform, name });
    setSelectedMarkets(markets);
    setButtonItem(name);
  };

  const handleNoMarketsBundleClick = () => {
    setSelectedMarkets([]);
    setButtonItem("No markets");
  };

  // Calculate total price
  const totalPrice = useMemo(() => {
    return getTotalPriceHandler(selectedMarkets, filterMarketsToTradingPlatform);
  }, [selectedMarkets, filterMarketsToTradingPlatform]);

  if (!selectedPlatform) {
    return (
      <p className="greyed-text">Please select a platform to choose market data</p>
    );
  }

  if (!tradingPlatformRef) {
    return (
      <p className="greyed-text">Market data not available for selected platform</p>
    );
  }

  if (filterMarketsToTradingPlatform.length === 0) {
    return (
      <p className="greyed-text">No market data options available for this platform</p>
    );
  }

  return (
    <div className="marketdata-selection-block">
      {bundleButtons.length > 0 && (
        <div className="marketdata-bundle">
          <span className="bundle-text">Bundle:</span>
          <div className="marketdata-button-group">
            {bundleButtons.map((button) => (
              <button
                key={`radio-${button.name}`}
                id={`md-bundle-${button.name}`}
                className={`bundle-button ${buttonItem === button.name ? "active" : ""}`}
                onClick={() => {
                  if (button.name === "No markets") {
                    handleNoMarketsBundleClick();
                  } else {
                    handleRequestMarketBundleChange({ name: button.name });
                  }
                }}
                disabled={button.disabled}
              >
                {button.displayLabel ?? button.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="marketdata-main-block">
        {groups.map((group) => (
          <MarketDataItem
            key={group}
            title={group}
            primaryEntitlements={[]}
            marketdataProviders={marketdataProviders}
            onMarketStatusChange={handleMarketStatusChange}
            entitlements={selectedMarkets}
            marketItems={filterMarketsToTradingPlatform}
            changingStatus={true}
            requestedMarketsData={null}
          />
        ))}
      </div>
      <div className="marketdata-cost-display">
        <strong>Market data monthly cost: {totalPrice}</strong>
      </div>
    </div>
  );
};

export default MarketDataSelection;

